// FacilitatorDetailsPage.tsx
import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Title, Card, Avatar, Badge, Button,
  Group, Stack, Text, Skeleton, Drawer, ActionIcon,
} from '@mantine/core';
import {
  IconArrowLeft, IconBriefcase2, IconFileText, IconLayoutDashboard, IconMapPin, IconPencil,
  IconRefresh, IconClipboardList,
} from '@tabler/icons-react';
import { resolveAvatarSrc, getInitialsFromName, reportAvatarLoadError } from '@/utils/avatar';
import Tabs from '@/components/ui/navigation/Tabs';
import {
  useFacilitatorService,
  FacilitatorModelView,
  useFacilitatorStatusService,
  FacilitatorStatus,
  useLocationService,
} from '@actschurch/ccms-sdk';
import Flex from '@/components/ui/layout/Flex';
import Tooltip from '@/components/ui/overlays/Tooltip';
import { KpiCard } from '@/components/advanced/Widgets/kpi';

// ─── Tab components ────────────────────────────────────────────────────────────
import OverviewTab      from './Tabs/OverviewTab';
import CoursesTab       from './Tabs/CourseTab';
import RegistrationsTab from './Tabs/RegistrationTab';
import LocationsTab     from './Tabs/LocationsTab';
import DocumentsTab     from './Tabs/DocumentTab';
import EditTab          from './Tabs/EditTabs';
import { tabStyle } from '@/components/ui/TabStyle';
import { UnlistedAssigneeCard } from '../../Components/UnlistedAssigneeCard';




export default function FacilitatorDetailsPage() {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Name carried through from the list page for someone assigned to course work who has no facilitator
   * record. The detail page cannot look it up, there is nothing to fetch, so the list passes it along.
   */
  const unlistedName = (location.state as { unlistedName?: string } | null)?.unlistedName ?? null;

  // ─── SDK services ──────────────────────────────────────────────────────────
  const Facilitators        = useFacilitatorService();
  const FacilitatorStatuses = useFacilitatorStatusService();
  const LocationService     = useLocationService();

  // ─── Page state ────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab]     = useState<string | null>('overview');
  const [loading, setLoading]         = useState(true);
  const [facilitator, setFacilitator] = useState<FacilitatorModelView | null>(null);

  // ── Edit drawer ──────────────────────────────────────────────────────────
  const [editOpen, setEditOpen] = useState(false);

  // ─── Lookup data — fetched once, passed as props to avoid duplicate calls ──
  const [allStatuses, setAllStatuses]   = useState<FacilitatorStatus[]>([]);
  const [allLocations, setAllLocations] = useState<any[]>([]);

  // ─── Fetch facilitator profile ─────────────────────────────────────────────
  const fetchFacilitator = useCallback(async () => {
    if (!id) { setLoading(false); return; }
    setLoading(true);
    try {
      const result = await Facilitators.getByUserId(id);
      setFacilitator(result);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [id, Facilitators]);

  // ─── Fetch lookups in parallel ─────────────────────────────────────────────
  const fetchLookups = useCallback(async () => {
    try {
      const [statuses, locations] = await Promise.all([
        FacilitatorStatuses.getAll(),
        LocationService.getAll(),
      ]);
      setAllStatuses(statuses);
      setAllLocations(locations);
    } catch (e) {
      console.error(e);
    }
  }, [FacilitatorStatuses, LocationService]);

  useEffect(() => { fetchFacilitator(); }, [fetchFacilitator]);
  useEffect(() => { fetchLookups();     }, [fetchLookups]);


  // ─── Derived display values ────────────────────────────────────────────────
  const displayName = (() => {
    if (!facilitator) return 'Unknown';
    const fromParts = [facilitator.firstName, facilitator.surname].filter(Boolean).join(' ').trim();
    if (fromParts) return fromParts;
    const display = facilitator.displayName?.trim();
    if (display && display !== facilitator.userId) return display;
    return facilitator.fullName?.trim() || 'Unknown';
  })();

  const displayAvatar = resolveAvatarSrc(facilitator?.avatar);

  // Counts backing the Overview stat bar
  const courseCount     = facilitator?.coursesCount            ?? 0;
  const enrolmentCount  = facilitator?.activeAssignmentsCount  ?? 0;
  const locationCount   = facilitator?.locationsCount          ?? 0;

  if (!id) return null;

  /**
   * No facilitator record behind the id. Either the person was never given a user in Identity, or the
   * record was removed while an assignment still points at them. Previously this fell through to the
   * normal layout and rendered a silent empty shell, so it now says what is wrong and offers the fix.
   */
  if (!loading && !facilitator) {
    return (
      <Container size="xl" py="xl">
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Title order={2} mb="md">Facilitator</Title>
        <UnlistedAssigneeCard
          userId={id}
          kind="facilitator"
          fallbackName={unlistedName}
          onCreated={fetchFacilitator}
        />
      </Container>
    );
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Container size="xl" py="xl">

      <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate(-1)}>
        Back
      </Button>

      {/* ── Header row: title + refresh (Edit lives on the profile card) ── */}
      <Group justify="space-between" mb="sm">
        <Title order={2}>Facilitator Details</Title>

       <Group gap="sm">
          <Flex justify="right" align="center">
            <Tooltip label="Refresh">
              <ActionIcon variant="subtle" onClick={fetchFacilitator} loading={loading} size="lg">
                <IconRefresh size={16} />
              </ActionIcon>
            </Tooltip>
          </Flex>
        </Group>
      </Group>

      {/* ── Profile card ───────────────────────────────────────────────────── */}
      <Card withBorder radius="md" padding="lg" mb="md">
        {loading ? (
          <Group gap="md" align="flex-start">
            <Skeleton circle height={72} />
            <Stack gap={8} style={{ flex: 1 }}>
              <Skeleton height={28} width="40%" />
              <Skeleton height={16} width="55%" />
              <Skeleton height={16} width="35%" />
            </Stack>
          </Group>
        ) : (
          <Group justify="space-between" align="flex-start">
            <Group gap="md" align="flex-start">

              {/* Initials are always the children so a broken image URL falls back to them
                  rather than Mantine's generic placeholder. */}
              <Avatar
                src={displayAvatar}
                alt={displayName}
                size={90}
                radius="md"
                imageProps={{ onError: () => reportAvatarLoadError(displayAvatar) }}
              >
                {getInitialsFromName(displayName)}
              </Avatar>

              <Stack gap={4}>
                <Title order={4}>{displayName}</Title>
                 <Text size="sm" c="dimmed" mt={4} maw={500}>
                  {facilitator?.bio || 'No bio provided'}
                </Text>

                {/* Status badge colored by the lookup statusColor */}
                <Group gap={6}>
                  <Badge
                    variant="light"
                    style={{
                      backgroundColor: facilitator?.statusColor
                        ? `${facilitator.statusColor}22`
                        : undefined,
                      color: facilitator?.statusColor ?? undefined,
                    }}
                  >
                    {facilitator?.statusName ?? 'Unknown Status'}
                  </Badge>
                  <Badge variant="outline" color="gray">
                    {enrolmentCount} active enrolment{enrolmentCount === 1 ? '' : 's'}
                  </Badge>
                </Group>

              </Stack>
            </Group>

            <Button
              variant="filled"
              leftSection={<IconPencil size={16} />}
              onClick={() => setEditOpen(true)}
              disabled={!facilitator}
            >
              Edit
            </Button>
          </Group>
        )}
      </Card>

      {/* ── Tabs — Overview added, Edit removed (lives in the drawer above) ── */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab
            value="overview"
            leftSection={<IconLayoutDashboard size={16} />}
            style={tabStyle(activeTab === 'overview')}
          >
            Overview
          </Tabs.Tab>
          <Tabs.Tab
            value="courses"
            leftSection={<IconBriefcase2 size={16} />}
            style={tabStyle(activeTab === 'courses')}
          >
            Courses
          </Tabs.Tab>
          <Tabs.Tab
            value="registrations"
            leftSection={<IconClipboardList size={16} />}
            style={tabStyle(activeTab === 'registrations')}
          >
            Registrations
          </Tabs.Tab>
          <Tabs.Tab
            value="locations"
            leftSection={<IconMapPin size={16} />}
            style={tabStyle(activeTab === 'locations')}
          >
            Locations
          </Tabs.Tab>
          <Tabs.Tab
            value="documents"
            leftSection={<IconFileText size={16} />}
            style={tabStyle(activeTab === 'documents')}
          >
            Documents
          </Tabs.Tab>
        </Tabs.List>

        {/* All tab panels share a single bordered card container */}
        <Card withBorder radius="md" padding="lg" mt="sm">

          {/* ── Overview tab ─────────────────────────────────────────────── */}
          <Tabs.Panel value="overview" pt="md">
            <OverviewTab
              facilitator={facilitator}
              active={activeTab === 'overview'}
              courseCount={courseCount}
              enrolmentCount={enrolmentCount}
              locationCount={locationCount}
              onTabChange={setActiveTab}
              onRemoved={() => navigate(-1)}
            />
          </Tabs.Panel>

          {/* ── Courses tab ──────────────────────────────────────────────── */}
          <Tabs.Panel value="courses" pt="md">
            <CoursesTab
              userId={id}
              active={activeTab === 'courses'}
              onFacilitatorChange={fetchFacilitator}
            />
          </Tabs.Panel>

          {/* ── Registrations tab ─────────────────────────────────────────── */}
          <Tabs.Panel value="registrations" pt="md">
            <RegistrationsTab
              userId={id}
              active={activeTab === 'registrations'}
            />
          </Tabs.Panel>

          {/* ── Locations tab ─────────────────────────────────────────────── */}
          <Tabs.Panel value="locations" pt="md">
            <LocationsTab
              userId={id}
              active={activeTab === 'locations'}
              onFacilitatorChange={fetchFacilitator}
            />
          </Tabs.Panel>

          {/* ── Documents tab ─────────────────────────────────────────────── */}
          <Tabs.Panel value="documents" pt="md">
            <DocumentsTab
              userId={id}
              active={activeTab === 'documents'}
              onFacilitatorChange={fetchFacilitator}
            />
          </Tabs.Panel>

        </Card>
      </Tabs>

      {/* ── Edit Drawer ── */}
      <Drawer
        opened={editOpen}
        onClose={() => setEditOpen(false)}
        title={
          <Group gap="xs">
            <IconPencil size={18} />
            <Text fw={600}>Edit Facilitator</Text>
          </Group>
        }
        position="right"
        size="md"
        padding="md"
      >
        {facilitator && (
          <EditTab
            facilitator={facilitator}
            statuses={allStatuses}
            onSaved={() => { fetchFacilitator(); setEditOpen(false); }}
          />
        )}
      </Drawer>
    </Container>
  );
}